/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authClient } from '@/api/authClient';

export type AuthUser = {
  userId: string;
  name: string;
  email: string;
  role: string;
} | null;

type SessionUser = {
  id: string;
  userId?: string;
  name?: string;
  email?: string;
  role?: string;
};

interface AuthContextType {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: AuthUser;
  loading: boolean;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isAdmin: false,
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
});

const STALE_TIME_HOURS = Number(import.meta.env.VITE_SESSION_STALE_TIME_IN_HOURS) || 4;

const mapUser = (sessionUser: SessionUser | undefined | null): AuthUser => {
  if (!sessionUser) return null;
  return {
    userId: sessionUser.userId ?? sessionUser.id,
    name: sessionUser.name ?? '',
    email: sessionUser.email ?? '',
    role: sessionUser.role ?? 'user',
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: sessionUser, isLoading } = useQuery({
    queryKey: ['auth'],
    queryFn: async () => {
      const { data } = await authClient.getSession();
      return (data?.user ?? null) as SessionUser | null;
    },
    staleTime: STALE_TIME_HOURS * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const user = mapUser(sessionUser);

  const loginMutation = useMutation({
    mutationFn: async ({ email, password, rememberMe }: { email: string; password: string; rememberMe: boolean }) => {
      const { error } = await authClient.signIn.email({ email, password, rememberMe });
      if (error) { console.error(error); throw new Error(error.message || 'Échec de la connexion.'); }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth'] });
      navigate('/');
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authClient.signOut(),
    onSuccess: () => {
      queryClient.removeQueries();
      navigate('/login');
    },
  });

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    loading: isLoading,
    login: async (email, password, rememberMe) => {
      await loginMutation.mutateAsync({ email, password, rememberMe });
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
